import type { StudyData } from "../components/types";
import { logger } from "./logger";
import rdn60Summaries from "../data/rdn60FindingSummaries.json";

/**
 * Short LLM-generated finding summaries for the worklist Category column.
 *
 * For busy studies (>= 4 findings) the raw finding list reads as illegible noise,
 * so we show a single descriptive phrase instead (e.g. "Renal stones with hepatic
 * and adrenal lesions"). The full list stays one hover away via the cell `title`.
 *
 * Quick-and-dirty scope: phrases are PRECOMPUTED OFFLINE for the fixed `rdn60`
 * demo cohort (see scripts/generateRdn60Summaries.ts) and committed as a static
 * map keyed by bare study uid.
 *
 * To extend to ALL cohorts later: compute the summary server-side, cache it on the
 * study row, and surface it as `study.findingSummary`. Then this helper reads that
 * field instead of the map and drops the cohort gate — its signature stays the same,
 * so the CaseTableRow / CaseRow callers do not change.
 */

/** Cohort the precomputed summaries cover (confirmed: review cohort id is `rdn60`). */
export const RDN60_COHORT_ID = "rdn60";

/** At/above this finding count the raw list is too long — summarize instead. */
export const SUMMARY_MIN_FINDINGS = 4;

const summaryMap = rdn60Summaries as Record<string, string>;

const bareUid = (studyId: string): string => studyId.split("::")[0] ?? studyId;

/**
 * Returns a short summary phrase to show in place of the finding list, or `null`
 * to mean "render the existing finding list unchanged".
 *
 * Callers should only invoke this for real cortex finding rows (not reporter /
 * running / failed / empty rows). `findingCount` is the study's detected-finding
 * count the row already computed.
 */
export function getFindingSummary(
  study: Pick<StudyData, "studyId">,
  cohortId: string | undefined,
  findingCount: number,
): string | null {
  if (cohortId !== RDN60_COHORT_ID) return null;
  if (findingCount < SUMMARY_MIN_FINDINGS) return null;

  const uid = bareUid(study.studyId);
  const phrase = summaryMap[uid];
  if (phrase) return phrase;

  // No precomputed phrase (e.g. a study added after the last generation run).
  // Fall back to the full list — but loudly, never a silent blank.
  logger.warn(
    `[findingSummary] no precomputed rdn60 summary for ${uid} (${findingCount} findings); showing full finding list`,
  );
  return null;
}

export interface SummarySegment {
  text: string;
  bold: boolean;
}

/**
 * Splits a summary phrase containing `**bold**` markers into ordered segments so
 * the worklist can render the finding terms bold and the connecting words plain.
 * Empty segments (e.g. from adjacent markers) are dropped.
 */
export function parseBoldSegments(text: string): SummarySegment[] {
  const segments: SummarySegment[] = [];
  // Capturing split: even indices are the surrounding plain text, odd indices
  // are the contents of a `**...**` pair.
  text.split(/\*\*(.*?)\*\*/g).forEach((part, i) => {
    if (part === "") return;
    segments.push({ text: part, bold: i % 2 === 1 });
  });
  return segments;
}
